<template>
  <div class="p-4">
    <div class="bg-white rounded-lg shadow-md">
      <div class="p-4 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-800">KYT Change Requests</h2>
        
        <!-- Filters Section -->
        <div class="mt-4 space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Employee Name</label>
              <input
                type="text"
                v-model="filters.employeeName"
                class="form-control"
                placeholder="Search by employee name"
              >
            </div>
            
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
              <input
                type="text"
                v-model="filters.siteName"
                class="form-control"
                placeholder="Search by site name"
              >
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                v-model="filters.startDate"
                class="form-control"
              >
            </div>
            
            <div class="form-group">
              <label class="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                v-model="filters.endDate"
                class="form-control"
              >
            </div>
          </div>
        </div>
      </div>
      
      <div class="table-responsive">
        <table class="display table-striped table-hover table-bordered table" id="basic-1">
          <thead>
            <tr>
              <th class="sorting_asc text-nowrap" scope="col">Sl No.</th>
              <th class="sorting_asc text-nowrap" scope="col">KYT Log No</th>
              <th class="sorting_asc text-nowrap" scope="col">Site Name</th>
              <th class="sorting_asc text-nowrap" scope="col">Employee Name</th>
              <th class="sorting_asc text-nowrap" scope="col">Status</th>
              <th class="sorting_asc text-nowrap" scope="col">Reason</th>
              <th class="sorting_asc text-nowrap" scope="col">Change Date</th>
              <th class="sorting_asc text-nowrap" scope="col">Initial Date</th>
              <th class="sorting_asc text-nowrap" scope="col">Images</th>
              <th class="sorting_asc text-nowrap" scope="col">Approved By</th>
              <th class="sorting_asc text-nowrap" scope="col">Approved At</th>
              <th class="sorting_asc text-nowrap" scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(request, index) in filteredRequests" :key="request._id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ index + 1 }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">{{ request.kytLogNo }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.siteName }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.empName }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="{
                  'px-2 py-1 text-xs font-semibold rounded-full text-black': true,
                  'bg-yellow-100': request.status === 'Pending',
                  'bg-green-100': request.status === 'Approved',
                  'bg-red-100': request.status === 'Rejected'
                }">
                  {{ request.status }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ request.reason }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(request.date) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ request.initialNFD ? formatDate(request.initialNFD) : '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div v-if="request.images && request.images.length > 0" class="flex items-center justify-center">
                  <button 
                    @click="openGallery(request.images)"
                    class="btn btn-light btn-sm"
                    :title="`View ${request.images.length} Image${request.images.length > 1 ? 's' : ''}`"
                  >
                    <i class="fa fa-images"></i>
                    <span class="ml-1 text-xs">{{ request.images.length }}</span>
                  </button>
                </div>
                <span v-else>-</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ request.approvedBy?.name || '-' }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(request.approvedAt) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                <button
                  v-if="request.status === 'Pending'"
                  @click="handleApprove(request._id)"
                  class="btn btn-success btn-sm"
                >
                  Approve
                </button>
                <button
                  v-if="request.status === 'Pending'"
                  @click="openRejectModal(request._id)"
                  class="btn btn-danger btn-sm"
                >
                  Reject
                </button>
              </td>
            </tr>
            <tr v-if="filteredRequests.length === 0">
              <td colspan="12" class="text-center py-4 text-muted">No change requests found</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Show total results count -->
      <div class="p-4 border-t border-gray-200">
        <p class="text-sm text-gray-600">
          Showing {{ filteredRequests.length }} of {{ changeRequests.length }} total requests
        </p>
      </div>
    </div>

    <!-- Reject Modal -->
    <div class="modal fade" id="rejectChangeModal" tabindex="-1" aria-labelledby="rejectModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="rejectModalLabel">Reject Change Request</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="rejectReason" class="form-label">Reason for Rejection</label>
              <textarea
                id="rejectReason"
                v-model="rejectReason"
                class="form-control"
                rows="3"
                placeholder="Enter reason for rejection"
                required
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="handleReject" :disabled="!rejectReason">
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Image Gallery Modal -->
    <div class="image-gallery-modal" v-if="showGallery" @click.self="closeGallery">
      <div class="gallery-content" :class="{ 'is-zoomed': isZoomed }">
        <!-- Header -->
        <div class="gallery-header">
          <div class="gallery-title">
            <span class="title">Image Gallery</span>
            <span class="counter">{{ currentImageIndex + 1 }} / {{ galleryImages.length }}</span>
          </div>
          <div class="gallery-actions">
            <button class="action-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'">
              <i :class="isFullscreen ? 'fa fa-compress' : 'fa fa-expand'"></i>
            </button>
            <button class="action-btn" @click="closeGallery">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Main Content -->
        <div class="gallery-main" 
             @wheel.prevent="handleZoom"
             @mousedown="startPan"
             @mousemove="pan"
             @mouseup="stopPan"
             @mouseleave="stopPan"
             @touchstart="handleTouchStart"
             @touchmove="handleTouchMove"
             @touchend="handleTouchEnd">
          
          <!-- Loading State -->
          <div class="loading-overlay" v-if="isImageLoading">
            <div class="loader"></div>
          </div>

          <!-- Error State -->
          <div class="error-overlay" v-if="imageError">
            <div class="error-message">
              <i class="fa fa-exclamation-circle"></i>
              <span>Failed to load image</span>
              <button class="retry-btn" @click="reloadImage">Retry</button>
            </div>
          </div>

          <!-- Image Container -->
          <div class="image-container" :style="containerStyle">
            <img 
              ref="mainImage"
              :src="currentGalleryImage" 
              :class="['main-image', { 'loading': isImageLoading }]"
              :style="imageStyle"
              @load="handleImageLoad"
              @error="handleImageError"
              draggable="false"
              alt="Gallery Image"
            />
          </div>

          <!-- Navigation Buttons -->
          <button 
            v-if="galleryImages.length > 1"
            class="nav-btn prev"
            @click="previousImage"
            :disabled="isImageLoading"
          >
            <i class="fa fa-chevron-left"></i>
          </button>
          <button 
            v-if="galleryImages.length > 1"
            class="nav-btn next"
            @click="nextImage"
            :disabled="isImageLoading"
          >
            <i class="fa fa-chevron-right"></i>
          </button>

          <!-- Zoom Controls -->
          <div class="zoom-controls">
            <button class="zoom-btn" @click="zoomOut" :disabled="zoomLevel <= 1">
              <i class="fa fa-search-minus"></i>
            </button>
            <span class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</span>
            <button class="zoom-btn" @click="zoomIn" :disabled="zoomLevel >= 5">
              <i class="fa fa-search-plus"></i>
            </button>
            <button class="zoom-btn" @click="resetZoom" :disabled="zoomLevel === 1">
              <i class="fa fa-undo"></i>
            </button>
          </div>
        </div>

        <!-- Thumbnails -->
        <div class="gallery-thumbnails" v-if="galleryImages.length > 1">
          <div class="thumbnails-scroll" ref="thumbnailsContainer">
            <div 
              v-for="(image, index) in galleryImages" 
              :key="index"
              class="thumbnail-item"
              :class="{ active: currentImageIndex === index }"
              @click="selectImage(index)"
            >
              <img 
                :src="image" 
                :alt="'Thumbnail ' + (index + 1)"
                loading="lazy"
              />
              <div class="thumbnail-overlay">
                <span>{{ index + 1 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue'
import axios from 'axios'
import { Modal } from 'bootstrap'

// Interfaces
interface KytId {
  _id: string | null
  siteName?: string
  status?: string
  date?: string
}

interface EmpId {
  _id: string
  email: string
}

interface ChangeRequest {
  _id: string
  kytId: KytId | null
  kytLogNo: string
  siteName: string
  empId: EmpId
  empName: string
  reason: string
  date: string
  status: string
  createdAt: string
  updatedAt: string
  __v: number
  images: string[]
  initialNFD: string | null
  approvedBy: {
    _id: string
    name: string
    email: string
  } | null
  approvedAt: string | null
}

// Update the interface to use only client coordinates
interface PointerCoordinates {
  clientX: number;
  clientY: number;
}

// State
const changeRequests = ref<ChangeRequest[]>([])
const rejectReason = ref('')
const selectedRequestId = ref('')
const baseUrl = process.env.VUE_APP_BASE_URL
const token = localStorage.getItem('token')

// Gallery state
const showGallery = ref(false)
const isFullscreen = ref(false)
const isZoomed = ref(false)
const mainImage = ref<HTMLImageElement | null>(null)
const thumbnailsContainer = ref<HTMLElement | null>(null)
const galleryImages = ref<string[]>([])
const currentImageIndex = ref(0)
const isImageLoading = ref(true)
const imageError = ref(false)

// Touch handling state
const touchStartX = ref(0)
const touchStartY = ref(0)
const initialPinchDistance = ref(0)
const zoomLevel = ref(1)
const panPosition = ref({ x: 0, y: 0 })
const isPanning = ref(false)
const startPanPosition = ref({ x: 0, y: 0 })
const lastMousePosition = ref({ x: 0, y: 0 })

// Add filter states
const filters = ref({
  employeeName: '',
  siteName: '',
  startDate: '',
  endDate: ''
})

// Computed
const currentGalleryImage = computed(() => {
  return galleryImages.value[currentImageIndex.value] || ''
})

const containerStyle = computed(() => ({
  cursor: isPanning.value ? 'grabbing' : (zoomLevel.value > 1 ? 'grab' : 'default'),
  overflow: zoomLevel.value > 1 ? 'hidden' : 'visible'
}))

const imageStyle = computed(() => ({
  transform: `translate(${panPosition.value.x}px, ${panPosition.value.y}px) scale(${zoomLevel.value})`,
  opacity: isImageLoading.value ? 0 : 1
}))

// Add computed property for filtered requests
const filteredRequests = computed(() => {
  return changeRequests.value.filter(request => {
    const matchesEmployeeName = !filters.value.employeeName || 
      request.empName.toLowerCase().includes(filters.value.employeeName.toLowerCase())
    
    const matchesSiteName = !filters.value.siteName || 
      request.siteName.toLowerCase().includes(filters.value.siteName.toLowerCase())
    
    const requestDate = new Date(request.date)
    const startDate = filters.value.startDate ? new Date(filters.value.startDate) : null
    const endDate = filters.value.endDate ? new Date(filters.value.endDate) : null
    
    const matchesDateRange = (!startDate || requestDate >= startDate) && 
      (!endDate || requestDate <= new Date(endDate.setHours(23, 59, 59, 999)))
    
    return matchesEmployeeName && matchesSiteName && matchesDateRange
  })
})

// Methods
const fetchChangeRequests = async () => {
  try {
    const response = await axios.get(`${baseUrl}api/operation/kyt/change-requests`)
    if (response.data.success) {
      changeRequests.value = response.data.data
    }
  } catch (error) {
    console.error('Error fetching change requests:', error)
    alert('Error fetching change requests')
  }
}

const handleApprove = async (requestId: string) => {
  try {
    const response = await axios.put(
      `${baseUrl}api/operation/kyt/change-requests/approve/${requestId}`,
      {}, // Empty body
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    )
    if (response.data.success) {
      alert('Change request approved successfully')
      await fetchChangeRequests()
    }
  } catch (error: any) {
    console.error('Error approving request:', error)
    alert(error.response?.data?.message || 'Error approving request')
  }
}

const openRejectModal = (requestId: string) => {
  selectedRequestId.value = requestId
  rejectReason.value = ''
  rejectModal?.show()
}

const handleReject = async () => {
  if (!rejectReason.value) {
    alert('Please provide a reason for rejection')
    return
  }

  try {
    const response = await axios.put(
      `${baseUrl}api/operation/kyt/change-requests/reject/${selectedRequestId.value}`,
      { reason: rejectReason.value }
    )
    if (response.data.success) {
      alert('Change request rejected successfully')
      rejectModal?.hide()
      await fetchChangeRequests()
    }
  } catch (error: any) {
    console.error('Error rejecting request:', error)
    alert(error.response?.data?.message || 'Error rejecting request')
  }
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-'
  
  try {
    let date: Date;
    
    // Handle simple DD-MM-YYYY format
    if (dateString.includes('-') && dateString.split('-').length === 3 && !dateString.includes('T')) {
      const [day, month, year] = dateString.split('-')
      date = new Date(`${year}-${month}-${day}`)
    }
    // Handle ISO format
    else if (dateString.includes('T')) {
      date = new Date(dateString)
    }
    // Handle full datetime string
    else {
      date = new Date(dateString)
    }

    if (isNaN(date.getTime())) return dateString // Return original if parsing fails

    // Format to DD-MM-YYYY
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear()

    return `${day}-${month}-${year}`
  } catch (error) {
    console.error('Error formatting date:', error)
    return dateString // Return original string if formatting fails
  }
}

// Gallery methods
const openGallery = (images: string[], startIndex: number = 0) => {
  galleryImages.value = images
  currentImageIndex.value = startIndex
  isImageLoading.value = true
  imageError.value = false
  showGallery.value = true
  resetZoom()
}

const closeGallery = () => {
  showGallery.value = false
  resetZoom()
}

const toggleFullscreen = async () => {
  try {
    if (!isFullscreen.value) {
      await document.documentElement.requestFullscreen()
      isFullscreen.value = true
    } else {
      await document.exitFullscreen()
      isFullscreen.value = false
    }
  } catch (error) {
    console.error('Fullscreen error:', error)
  }
}

const handleImageLoad = () => {
  isImageLoading.value = false
  imageError.value = false
}

const handleImageError = () => {
  isImageLoading.value = false
  imageError.value = true
}

const reloadImage = () => {
  isImageLoading.value = true
  imageError.value = false
  const currentImage = galleryImages.value[currentImageIndex.value]
  galleryImages.value[currentImageIndex.value] = `${currentImage}?t=${Date.now()}`
}

// Navigation methods
const nextImage = () => {
  if (isImageLoading.value) return
  isImageLoading.value = true
  imageError.value = false
  resetZoom()
  if (currentImageIndex.value < galleryImages.value.length - 1) {
    currentImageIndex.value++
  } else {
    currentImageIndex.value = 0
  }
}

const previousImage = () => {
  if (isImageLoading.value) return
  isImageLoading.value = true
  imageError.value = false
  resetZoom()
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--
  } else {
    currentImageIndex.value = galleryImages.value.length - 1
  }
}

const selectImage = (index: number) => {
  isImageLoading.value = true
  imageError.value = false
  currentImageIndex.value = index
  resetZoom()
}

// Zoom and pan methods
const handleZoom = (event: WheelEvent) => {
  if (isImageLoading.value) return
  
  const delta = event.deltaY * -0.01
  const newZoom = Math.max(1, Math.min(5, zoomLevel.value + delta))
  
  if (newZoom !== zoomLevel.value) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    
    const scale = newZoom / zoomLevel.value
    
    panPosition.value = {
      x: x - (x - panPosition.value.x) * scale,
      y: y - (y - panPosition.value.y) * scale
    }
    
    zoomLevel.value = newZoom
  }
}

const zoomIn = () => {
  if (zoomLevel.value < 5) {
    zoomLevel.value += 0.5
  }
}

const zoomOut = () => {
  if (zoomLevel.value > 1) {
    zoomLevel.value -= 0.5
  }
}

const resetZoom = () => {
  zoomLevel.value = 1
  panPosition.value = { x: 0, y: 0 }
}

// Update the pan handlers to use only client coordinates
const startPan = (event: MouseEvent | PointerCoordinates) => {
  if (zoomLevel.value > 1) {
    isPanning.value = true
    lastMousePosition.value = { 
      x: event.clientX,
      y: event.clientY
    }
    startPanPosition.value = { ...panPosition.value }
  }
}

const pan = (event: MouseEvent | PointerCoordinates) => {
  if (!isPanning.value) return
  
  const deltaX = event.clientX - lastMousePosition.value.x
  const deltaY = event.clientY - lastMousePosition.value.y
  
  panPosition.value = {
    x: startPanPosition.value.x + deltaX,
    y: startPanPosition.value.y + deltaY
  }
}

const stopPan = () => {
  isPanning.value = false
}

const handleTouchStart = (event: TouchEvent) => {
  if (event.touches.length === 2) {
    // Pinch to zoom
    const touch1 = event.touches[0]
    const touch2 = event.touches[1]
    initialPinchDistance.value = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    )
  } else if (event.touches.length === 1) {
    // Single touch for panning
    const touch = event.touches[0]
    touchStartX.value = touch.clientX
    touchStartY.value = touch.clientY
    if (zoomLevel.value > 1) {
      startPan({
        clientX: touch.clientX,
        clientY: touch.clientY
      })
    }
  }
}

const handleTouchMove = (event: TouchEvent) => {
  if (event.touches.length === 2) {
    // Handle pinch zoom
    const touch1 = event.touches[0]
    const touch2 = event.touches[1]
    const currentDistance = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    )
    
    const scale = currentDistance / initialPinchDistance.value
    const newZoom = Math.max(1, Math.min(5, zoomLevel.value * scale))
    
    if (newZoom !== zoomLevel.value) {
      zoomLevel.value = newZoom
    }
    
    initialPinchDistance.value = currentDistance
  } else if (event.touches.length === 1 && isPanning.value) {
    // Handle pan
    const touch = event.touches[0]
    pan({
      clientX: touch.clientX,
      clientY: touch.clientY
    })
  }
}

const handleTouchEnd = () => {
  stopPan()
  initialPinchDistance.value = 0
}

// Keyboard navigation
const handleKeyPress = (e: KeyboardEvent) => {
  if (!showGallery.value) return
  
  switch (e.key) {
    case 'ArrowLeft':
      previousImage()
      break
    case 'ArrowRight':
      nextImage()
      break
    case 'Escape':
      if (!isFullscreen.value) {
        closeGallery()
      }
      break
  }
}

// Lifecycle hooks
let rejectModal: any = null

onMounted(() => {
  fetchChangeRequests()
  rejectModal = new Modal(document.getElementById('rejectChangeModal'))
  
  window.addEventListener('keydown', handleKeyPress)
  document.addEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement
  })
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeyPress)
  document.removeEventListener('fullscreenchange', () => {})
})
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table th {
  font-weight: 600;
  background-color: #f8f9fa;
}

.table td {
  vertical-align: middle;
}

@media (max-width: 640px) {
  .table {
    display: block;
    width: 100%;
  }
  
  th, td {
    min-width: 120px;
  }
}

/* Gallery Modal Styles */
.image-gallery-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.gallery-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: white;
}

/* Header Styles */
.gallery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
}

.gallery-title {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.gallery-title .title {
  font-size: 1.25rem;
  font-weight: 500;
}

.gallery-title .counter {
  color: #9ca3af;
  font-size: 0.875rem;
}

.gallery-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Main Content Styles */
.gallery-main {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.image-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.main-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform 0.2s ease-out;
  will-change: transform;
}

.main-image.loading {
  opacity: 0;
}

/* Navigation Buttons */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.nav-btn.prev {
  left: 1rem;
}

.nav-btn.next {
  right: 1rem;
}

.nav-btn:hover:not(:disabled) {
  background: rgba(0, 0, 0, 0.8);
  transform: translateY(-50%) scale(1.1);
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Zoom Controls */
.zoom-controls {
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.6);
  padding: 0.5rem;
  border-radius: 8px;
  backdrop-filter: blur(4px);
}

.zoom-btn {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.zoom-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
}

.zoom-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.zoom-level {
  min-width: 60px;
  text-align: center;
  font-size: 0.875rem;
  color: #e5e7eb;
}

/* Thumbnails */
.gallery-thumbnails {
  background: rgba(0, 0, 0, 0.5);
  padding: 1rem;
  backdrop-filter: blur(10px);
}

.thumbnails-scroll {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) transparent;
  padding-bottom: 0.5rem;
}

.thumbnail-item {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-item.active {
  transform: scale(1.05);
  box-shadow: 0 0 0 2px #3b82f6;
}

.thumbnail-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.thumbnail-item:hover .thumbnail-overlay,
.thumbnail-item.active .thumbnail-overlay {
  opacity: 1;
}

/* Loading and Error States */
.loading-overlay,
.error-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

.error-message {
  text-align: center;
  color: #ef4444;
}

.error-message i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.retry-btn {
  margin-top: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-btn:hover {
  background: #dc2626;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Mobile Optimizations */
@media (max-width: 768px) {
  .gallery-header {
    padding: 0.75rem;
  }

  .nav-btn {
    width: 40px;
    height: 40px;
  }

  .zoom-controls {
    bottom: 1rem;
  }

  .thumbnail-item {
    width: 60px;
    height: 60px;
  }
}

/* Add these new styles */
.form-control {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  transition: border-color 0.15s ease-in-out;
}

.form-control:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.form-group {
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .grid-cols-2 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
  
  .form-group {
    margin-bottom: 0.75rem;
  }
}

/* Improve table styles */
.table {
  width: 100%;
  margin-bottom: 1rem;
  background-color: transparent;
  border-collapse: collapse;
}

.table th {
  padding: 0.75rem;
  font-weight: 600;
  background-color: #f3f4f6;
  border-bottom: 2px solid #e5e7eb;
}

.table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>

